import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Loader2, Save, Upload, X, CheckCircle, AlertCircle, Wand2, RotateCcw, Plus, Trash2 } from 'lucide-react';
import { rawFetch } from '../../services/api';

interface Capture {
  id: number;
  tenantId: number;
  title: string;
  description: string | null;
  slug: string;
  mediaType?: 'track' | 'video' | 'stream' | 'youtube';
  backgroundMode?: 'automatic' | 'solid' | 'image';
  backgroundColor?: string | null;
  backgroundImageS3Uri?: string | null;
  backgroundOverlayOpacity?: number | null;
  textColorMode?: 'automatic' | 'solid';
  buttonColorMode?: 'automatic' | 'solid';
  buttonBgColor?: string | null;
  buttonFgColor?: string | null;
  imageS3Uri: string | null;
  videoS3Uri?: string | null;
  videoThumbnailS3Uri?: string | null;
  youtubeAspectRatio?: '16:9' | '9:16';
  membershipId: number | null;
  vaultId: number | null;
  trackId: number | null;
  facebookPixel: string | null;
  aboutThisTrack?: string | null;
  sharingPrompt?: string | null;
  sharingSuggestions?: unknown;
  createdAt: number;
  updatedAt: number;
}

interface Track {
  id: number;
  title: string;
  trackReference: string;
  verificationStatus: string;
  songLength: number | null;
  previewStart: number | null;
  previewLength: number | null;
  isrc?: string | null;
  hasLyrics?: boolean;
  streamLinks?: Array<{ service: string; url: string }>;
}

interface SharingSuggestionDraft {
  id: string;
  label: string;
  message: string;
  sentiment: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';
}

interface VaultOption {
  id: number;
  name: string | null;
  status: string;
  coverUrl: string | null;
}

interface Membership {
  id: number;
  name: string;
  description: string | null;
}

interface CaptureFormProps {
  slug: string;
  capture: Capture | null;
  onBack: () => void;
  onSuccess: () => void;
}

interface UploadProgress {
  status: 'idle' | 'creating' | 'presigning' | 'uploading' | 'confirming' | 'complete' | 'error';
  progress: number;
  error: string | null;
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
const WEBP_QUALITY = 0.82;
const DEFAULT_VALUE_PROP = 'Collect this track from {{artist}} and stay connected for future releases.';
const DEFAULT_SHARING_PROMPT = 'Got an opinion? Sharing helps this artist find their next fan.';

function createSuggestionId(): string {
  return `share_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function parseInitialSharingSuggestions(value: unknown): SharingSuggestionDraft[] {
  const fromArray = (input: unknown): SharingSuggestionDraft[] => {
    if (!Array.isArray(input)) return [];
    return input
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const row = entry as Record<string, unknown>;
        const label = typeof row.label === 'string' ? row.label.trim() : '';
        const message = typeof row.message === 'string' ? row.message.trim() : '';
        const sentimentRaw = typeof row.sentiment === 'string' ? row.sentiment.trim().toUpperCase() : '';
        const sentiment = sentimentRaw === 'POSITIVE' || sentimentRaw === 'NEUTRAL' || sentimentRaw === 'NEGATIVE'
          ? sentimentRaw
          : 'NEUTRAL';
        if (!label || !message) return null;
        const id = typeof row.id === 'string' && row.id.trim().length > 0 ? row.id.trim() : createSuggestionId();
        return {
          id,
          label: label.slice(0, 24),
          message: message.slice(0, 280),
          sentiment,
        };
      })
      .filter((entry): entry is SharingSuggestionDraft => entry !== null)
      .slice(0, 8);
  };

  if (Array.isArray(value)) {
    return fromArray(value);
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return fromArray(parsed);
    } catch {
      return [];
    }
  }

  return [];
}

function loadImage(file: File): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => resolve({ img, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

function encodeToWebP(img: HTMLImageElement, width: number, height: number, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) { reject(new Error('Canvas context not available')); return; }
    ctx.drawImage(img, 0, 0, width, height);
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Failed to encode image')),
      'image/webp',
      quality
    );
  });
}

export default function CaptureForm({ slug, capture, onBack, onSuccess }: CaptureFormProps) {
  const [title, setTitle] = useState(capture?.title || '');
  const [description, setDescription] = useState(capture?.description || '');
  const [captureSlug, setCaptureSlug] = useState(capture?.slug || '');
  const [membershipId, setMembershipId] = useState<number | null>(capture?.membershipId || null);
  const [vaultId, setVaultId] = useState<number | null>((capture as any)?.vaultId || null);
  const [mediaType, setMediaType] = useState<'track' | 'video' | 'stream' | 'youtube'>(
    (capture as any)?.mediaType === 'video'
      ? 'video'
      : ((capture as any)?.mediaType === 'stream' ? 'stream' : ((capture as any)?.mediaType === 'youtube' ? 'youtube' : 'track'))
  );
  const [trackId, setTrackId] = useState<number | null>(capture?.trackId || null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoFileName, setVideoFileName] = useState<string | null>(null);
  const [existingVideoS3Uri, setExistingVideoS3Uri] = useState<string | null>((capture as any)?.videoS3Uri || null);
  const [videoThumbnailFile, setVideoThumbnailFile] = useState<File | null>(null);
  const [videoThumbnailPreview, setVideoThumbnailPreview] = useState<string | null>(null);
  const [existingVideoThumbnailS3Uri, setExistingVideoThumbnailS3Uri] = useState<string | null>((capture as any)?.videoThumbnailS3Uri || null);
  const [youtubeUrl, setYoutubeUrl] = useState<string>((capture as any)?.mediaType === 'youtube' ? String((capture as any)?.videoS3Uri || '') : '');
  const [youtubeAspectRatio, setYoutubeAspectRatio] = useState<'16:9' | '9:16'>(
    (capture as any)?.mediaType === 'youtube' && (capture as any)?.youtubeAspectRatio === '9:16' ? '9:16' : '16:9'
  );
  const [facebookPixel, setFacebookPixel] = useState(capture?.facebookPixel || '');
  const [buttonText, setButtonText] = useState((capture as any)?.buttonText || 'Collect this Track');
  const [buttonDelay, setButtonDelay] = useState((capture as any)?.buttonDelay ?? 10);
  const [valueProp, setValueProp] = useState((capture as any)?.valueProp || DEFAULT_VALUE_PROP);
  const [collectButtonText, setCollectButtonText] = useState((capture as any)?.collectButtonText || 'Collect');
  const [aboutThisTrack, setAboutThisTrack] = useState((capture as any)?.aboutThisTrack || '');
  const [sharingPrompt, setSharingPrompt] = useState((capture as any)?.sharingPrompt || DEFAULT_SHARING_PROMPT);
  const [sharingSuggestions, setSharingSuggestions] = useState<SharingSuggestionDraft[]>(
    parseInitialSharingSuggestions((capture as any)?.sharingSuggestions)
  );
  const [sharingNotice, setSharingNotice] = useState('');
  const [sharingError, setSharingError] = useState('');
  const [analysingLyrics, setAnalysingLyrics] = useState(false);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [lastGeneratedAboutSnapshot, setLastGeneratedAboutSnapshot] = useState<string>(() => {
    const initialAbout = String((capture as any)?.aboutThisTrack || '').trim();
    const initialSuggestions = parseInitialSharingSuggestions((capture as any)?.sharingSuggestions);
    return initialSuggestions.length > 0 ? initialAbout : '';
  });
  const [backgroundMode, setBackgroundMode] = useState<'automatic' | 'solid' | 'image'>(
    (capture as any)?.backgroundMode === 'solid'
      ? 'solid'
      : ((capture as any)?.backgroundMode === 'image' ? 'image' : 'automatic')
  );
  const [backgroundColor, setBackgroundColor] = useState<string>((capture as any)?.backgroundColor || '#0D0D0D');
  const [backgroundOverlayOpacity, setBackgroundOverlayOpacity] = useState<number>((capture as any)?.backgroundOverlayOpacity ?? 35);
  const [textColorMode, setTextColorMode] = useState<'automatic' | 'solid'>((capture as any)?.textColorMode === 'solid' ? 'solid' : 'automatic');
  const [textColor, setTextColor] = useState<string>((capture as any)?.textColor || '#FFFFFF');
  const [buttonColorMode, setButtonColorMode] = useState<'automatic' | 'solid'>((capture as any)?.buttonColorMode === 'solid' ? 'solid' : 'automatic');
  const [buttonBgColor, setButtonBgColor] = useState<string>((capture as any)?.buttonBgColor || (capture as any)?.buttonColor || '#00F5FF');
  const [buttonFgColor, setButtonFgColor] = useState<string>((capture as any)?.buttonFgColor || '#0D0D0D');
  const [audioStart, setAudioStart] = useState((capture as any)?.audioStart ?? 0);
  const [playbackType, setPlaybackType] = useState<'preview' | 'full'>((capture as any)?.playbackType || 'preview');
  const [playbackLength, setPlaybackLength] = useState<number>((capture as any)?.playbackLength ?? 30);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [vaults, setVaults] = useState<VaultOption[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loadingVaultTracks, setLoadingVaultTracks] = useState(false);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Image state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [backgroundImageFile, setBackgroundImageFile] = useState<File | null>(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState<string | null>(null);
  const [existingBackgroundImageS3Uri, setExistingBackgroundImageS3Uri] = useState<string | null>((capture as any)?.backgroundImageS3Uri || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoThumbnailInputRef = useRef<HTMLInputElement>(null);
  const backgroundImageInputRef = useRef<HTMLInputElement>(null);

  // Upload progress modal state
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    status: 'idle',
    progress: 0,
    error: null,
  });

  const isEdit = !!capture;

  useEffect(() => {
    async function loadOptions() {
      try {
        const [vaultsRes, membershipsRes] = await Promise.all([
          rawFetch(`/api/public/tenants/vaults?limit=50`, { headers: { 'X-Tenant-Slug': slug } }),
          rawFetch(`/api/public/tenants/memberships`, { headers: { 'X-Tenant-Slug': slug } }),
        ]);

        if (vaultsRes.ok) {
          const vaultsJson = await vaultsRes.json() as { data: VaultOption[] };
          // Only show vaults that are SCHEDULED or PRESSED
          const eligible = vaultsJson.data.filter((v) => {
            const s = (v.status || '').toUpperCase();
            return s === 'SCHEDULED' || s === 'PRESSED';
          });
          setVaults(eligible);
        }
        if (membershipsRes.ok) {
          const membershipsJson = await membershipsRes.json() as { data: Membership[] };
          setMemberships(membershipsJson.data);
        }
      } catch (err) {
        console.error('[CaptureForm] Failed to load options:', err);
      } finally {
        setLoadingOptions(false);
      }
    }
    loadOptions();
  }, [slug]);

  // Load tracks for the selected vault
  useEffect(() => {
    if (!vaultId) {
      setTracks([]);
      return;
    }
    let cancelled = false;
    async function loadVaultTracks() {
      setLoadingVaultTracks(true);
      try {
        const res = await rawFetch(`/api/public/tenants/vaults/${vaultId}`, {
          headers: { 'X-Tenant-Slug': slug },
        });
        if (res.ok && !cancelled) {
          const json = await res.json() as { data: { tracks?: any[] } };
          const vaultTracks: Track[] = (json.data.tracks || []).map((t: any) => ({
            id: t.trackId || t.id,
            title: t.title || t.trackTitle || `Track #${t.trackId || t.id}`,
            trackReference: t.trackReference || '',
            verificationStatus: t.verificationStatus || 'VERIFIED',
            songLength: t.songLength || null,
            previewStart: t.previewStart ?? null,
            previewLength: t.previewLength ?? null,
            isrc: t.isrc || null,
            hasLyrics: !!t.hasLyrics,
            streamLinks: Array.isArray(t.streamLinks) ? t.streamLinks : [],
          }));
          setTracks(vaultTracks);
        }
      } catch (err) {
        console.error('[CaptureForm] Failed to load vault tracks:', err);
      } finally {
        if (!cancelled) setLoadingVaultTracks(false);
      }
    }
    loadVaultTracks();
    return () => { cancelled = true; };
  }, [vaultId, slug]);

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60);
  };

  const streamEligibleTracks = tracks.filter((t) => !!t.isrc && Array.isArray(t.streamLinks) && t.streamLinks.length > 0);
  const availableTracks = mediaType === 'stream' ? streamEligibleTracks : tracks;
  const selectedTrack = tracks.find((t) => String(t.id) === String(trackId || '')) || null;
  const canAttemptLyricsAnalysis = (mediaType === 'track' || mediaType === 'stream') && !!trackId;
  const hasLyricsForSelectedTrack = !!selectedTrack?.hasLyrics;
  const suggestionsNeedRefresh = sharingSuggestions.length > 0
    && lastGeneratedAboutSnapshot.length > 0
    && aboutThisTrack.trim().length > 0
    && aboutThisTrack.trim() !== lastGeneratedAboutSnapshot;

  const handleSuggestionChange = (id: string, field: 'label' | 'message' | 'sentiment', value: string) => {
    setSharingSuggestions((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: field === 'label'
                ? value.slice(0, 24)
                : (field === 'message'
                    ? value.slice(0, 280)
                    : ((value === 'POSITIVE' || value === 'NEUTRAL' || value === 'NEGATIVE') ? value : 'NEUTRAL')),
            }
          : row
      )
    );
  };

  const handleRemoveSuggestion = (id: string) => {
    setSharingSuggestions((prev) => prev.filter((row) => row.id !== id));
  };

  const handleAddSuggestion = () => {
    if (sharingSuggestions.length >= 8) return;
    setSharingSuggestions((prev) => [...prev, { id: createSuggestionId(), label: '', message: '', sentiment: 'NEUTRAL' }]);
  };

  const handleAnalyseLyrics = async () => {
    if (!canAttemptLyricsAnalysis || !trackId || analysingLyrics) return;
    if (aboutThisTrack.trim().length > 0) {
      const confirmed = window.confirm('Replace the current About This Track text with a new lyrics analysis?');
      if (!confirmed) return;
    }

    setSharingNotice('');
    setSharingError('');
    setAnalysingLyrics(true);

    try {
      const res = await rawFetch('/api/public/tenants/captures/analyse-lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({
          trackId,
          vaultId,
          captureTitle: title,
          captureDescription: description,
        }),
      });

      const json = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        setSharingError(json?.error || 'Unable to analyse lyrics right now.');
        return;
      }

      if (!json?.data?.lyricsAvailable) {
        setSharingNotice(json?.data?.message || 'Lyrics are unavailable right now. Please complete About This Track manually.');
        return;
      }

      const generatedText = String(json?.data?.aboutThisTrack || '').trim();
      if (!generatedText) {
        setSharingError('Lyrics analysis returned no usable text. Please try again or write it manually.');
        return;
      }

      setAboutThisTrack(generatedText);
      setSharingNotice('Lyrics analysed. Review and edit About This Track before generating suggestions.');
    } catch {
      setSharingError('Unable to analyse lyrics right now. Please try again.');
    } finally {
      setAnalysingLyrics(false);
    }
  };

  const handleGenerateSuggestions = async () => {
    if (generatingSuggestions) return;

    const about = aboutThisTrack.trim();
    if (!about) {
      setSharingError('Add About This Track before generating suggestions.');
      return;
    }

    if (sharingSuggestions.length > 0) {
      const confirmed = window.confirm('Replace your current suggestions with newly generated suggestions?');
      if (!confirmed) return;
    }

    setSharingNotice('');
    setSharingError('');
    setGeneratingSuggestions(true);

    try {
      const res = await rawFetch('/api/public/tenants/captures/generate-suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({
          aboutThisTrack: about,
          trackId,
          vaultId,
          captureTitle: title,
          captureDescription: description,
        }),
      });

      const json = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        setSharingError(json?.error || 'Unable to generate suggestions right now.');
        return;
      }

      const suggestions = parseInitialSharingSuggestions(json?.data?.suggestions);
      if (suggestions.length !== 8) {
        setSharingError('AI returned an invalid suggestion format. Please try again.');
        return;
      }

      setSharingSuggestions(suggestions);
      setLastGeneratedAboutSnapshot(about);
      setSharingNotice('Generated 8 suggestions. Edit or remove any suggestion before saving.');
    } catch {
      setSharingError('Unable to generate suggestions right now. Please try again.');
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  const isValidYouTubeUrl = (value: string): boolean => {
    const raw = value.trim();
    if (!raw) return false;
    try {
      const url = new URL(raw);
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      if (host === 'youtu.be') {
        const id = url.pathname.replace(/^\//, '');
        return /^[A-Za-z0-9_-]{11}$/.test(id);
      }
      if (host === 'youtube.com' || host === 'm.youtube.com') {
        if (url.pathname === '/watch') {
          const id = url.searchParams.get('v') || '';
          return /^[A-Za-z0-9_-]{11}$/.test(id);
        }
        const parts = url.pathname.split('/').filter(Boolean);
        if (parts[0] === 'shorts' || parts[0] === 'embed') {
          return /^[A-Za-z0-9_-]{11}$/.test(parts[1] || '');
        }
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (!isEdit || captureSlug === generateSlug(capture?.title || '')) {
      setCaptureSlug(generateSlug(val));
    }
  };

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic validation
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setFieldErrors((prev) => ({ ...prev, image: 'Invalid file type. Allowed: JPEG, PNG, WebP' }));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setFieldErrors((prev) => ({ ...prev, image: 'File too large. Maximum 10MB' }));
      return;
    }

    setFieldErrors((prev) => { const n = { ...prev }; delete n.image; return n; });
    setImageFile(file);

    // Generate preview
    const url = URL.createObjectURL(file);
    setImagePreview(url);
  }, []);

  const removeImage = useCallback(() => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [imagePreview]);

  const handleBackgroundImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setFieldErrors((prev) => ({ ...prev, backgroundImage: 'Invalid file type. Allowed: JPEG, PNG, WebP' }));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setFieldErrors((prev) => ({ ...prev, backgroundImage: 'File too large. Maximum 10MB' }));
      return;
    }

    setFieldErrors((prev) => { const n = { ...prev }; delete n.backgroundImage; return n; });
    setBackgroundImageFile(file);

    const url = URL.createObjectURL(file);
    setBackgroundImagePreview(url);
  }, []);

  const removeBackgroundImage = useCallback(() => {
    setBackgroundImageFile(null);
    if (backgroundImagePreview) URL.revokeObjectURL(backgroundImagePreview);
    setBackgroundImagePreview(null);
    setExistingBackgroundImageS3Uri(null);
    if (backgroundImageInputRef.current) backgroundImageInputRef.current.value = '';
  }, [backgroundImagePreview]);

  const handleVideoThumbnailSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setFieldErrors((prev) => ({ ...prev, videoThumbnail: 'Invalid file type. Allowed: JPEG, PNG, WebP' }));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setFieldErrors((prev) => ({ ...prev, videoThumbnail: 'File too large. Maximum 10MB' }));
      return;
    }

    setFieldErrors((prev) => { const n = { ...prev }; delete n.videoThumbnail; return n; });
    setVideoThumbnailFile(file);

    const url = URL.createObjectURL(file);
    setVideoThumbnailPreview(url);
  }, []);

  const removeVideoThumbnail = useCallback(() => {
    setVideoThumbnailFile(null);
    if (videoThumbnailPreview) URL.revokeObjectURL(videoThumbnailPreview);
    setVideoThumbnailPreview(null);
    setExistingVideoThumbnailS3Uri(null);
    if (videoThumbnailInputRef.current) videoThumbnailInputRef.current.value = '';
  }, [videoThumbnailPreview]);

  const handleVideoSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      setFieldErrors((prev) => ({ ...prev, video: 'Invalid video type. Allowed: MP4, WebM, MOV' }));
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      setFieldErrors((prev) => ({ ...prev, video: 'Video too large. Maximum 500MB' }));
      return;
    }

    setFieldErrors((prev) => { const n = { ...prev }; delete n.video; return n; });
    setVideoFile(file);
    setVideoFileName(file.name);
  }, []);

  const uploadCaptureVideo = async (captureId: number, file: File): Promise<boolean> => {
    try {
      const presignRes = await rawFetch(`/api/public/tenants/captures/${captureId}/video/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({ contentType: file.type, fileSize: file.size }),
      });
      if (!presignRes.ok) return false;
      const presignJson = await presignRes.json() as { data: { uploadUrl: string; path: string } };

      const uploadRes = await fetch(presignJson.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadRes.ok) return false;

      const confirmRes = await rawFetch(`/api/public/tenants/captures/${captureId}/video/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({ path: presignJson.data.path }),
      });
      return confirmRes.ok;
    } catch {
      return false;
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!title.trim()) errors.title = 'Title is required';
    if (!description.trim()) errors.description = 'Description is required';
    if (!captureSlug.trim()) errors.slug = 'Slug is required';
    if (!membershipId) errors.membership = 'Membership is required';
    if (!vaultId) errors.vault = 'Pressing is required';
    if ((mediaType === 'track' || mediaType === 'stream') && !trackId) errors.track = 'Track is required';
    if (mediaType === 'stream' && vaultId && streamEligibleTracks.length === 0) {
      errors.vault = 'Selected pressing must include at least one track with ISRC and streaming links';
    }
    if (mediaType === 'stream' && trackId && !streamEligibleTracks.some((t) => t.id === trackId)) {
      errors.track = 'Select a track that has ISRC and streaming links';
    }
    if (mediaType === 'video' && !videoFile && !existingVideoS3Uri) errors.video = 'Video upload is required';
    if (mediaType === 'video' && !videoThumbnailFile && !existingVideoThumbnailS3Uri) errors.videoThumbnail = 'Video thumbnail is required';
    if (mediaType === 'youtube' && !isValidYouTubeUrl(youtubeUrl)) errors.youtubeUrl = 'A valid YouTube URL is required';
    if (backgroundMode === 'image' && !backgroundImageFile && !existingBackgroundImageS3Uri) {
      errors.backgroundImage = 'Background image is required when background mode is Image';
    }
    if (aboutThisTrack.length > 2000) {
      errors.aboutThisTrack = 'About This Track must be 2,000 characters or fewer';
    }
    if (sharingPrompt.trim().length === 0) {
      errors.sharingPrompt = 'Sharing prompt is required';
    }
    if (sharingPrompt.length > 280) {
      errors.sharingPrompt = 'Sharing prompt must be 280 characters or fewer';
    }
    if (sharingSuggestions.length > 8) {
      errors.sharingSuggestions = 'A maximum of eight suggestions is allowed';
    }
    sharingSuggestions.forEach((row, index) => {
      const label = row.label.trim();
      const message = row.message.trim();
      const sentiment = row.sentiment;
      if (!label) errors[`suggestionLabel_${index}`] = 'Label is required';
      if (!message) errors[`suggestionMessage_${index}`] = 'Message is required';
      if (label.length > 24) errors[`suggestionLabel_${index}`] = 'Label must be 24 characters or fewer';
      if (message.length > 280) errors[`suggestionMessage_${index}`] = 'Message must be 280 characters or fewer';
      if (sentiment !== 'POSITIVE' && sentiment !== 'NEUTRAL' && sentiment !== 'NEGATIVE') {
        errors[`suggestionSentiment_${index}`] = 'Sentiment must be Positive, Neutral, or Negative';
      }
    });

    // Validate preview settings
    if (mediaType === 'track' && trackId && playbackType === 'preview') {
      const selectedTrack = tracks.find((t) => String(t.id) === String(trackId));
      const songLen = selectedTrack?.songLength || 0;
      if (playbackLength < 30) {
        errors.playbackLength = 'Playback length must be at least 30 seconds';
      }
      if (songLen > 0 && audioStart + playbackLength > songLen) {
        errors.playbackLength = 'Start + length exceeds track duration';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const uploadCaptureImage = async (
    captureId: number,
    file: File,
    variant: 'image' | 'background-image' | 'video-thumbnail' = 'image'
  ): Promise<boolean> => {
    setUploadProgress({ status: 'presigning', progress: 10, error: null });

    // Load image for encoding
    let imgData: { img: HTMLImageElement; width: number; height: number };
    try {
      imgData = await loadImage(file);
    } catch {
      setUploadProgress({ status: 'error', progress: 0, error: 'Failed to read image.' });
      return false;
    }

    const { img, width, height } = imgData;

    // Encode to WebP
    setUploadProgress({ status: 'presigning', progress: 15, error: null });

    let optimizedBlob: Blob;
    try {
      optimizedBlob = await encodeToWebP(img, width, height, WEBP_QUALITY);
      if (optimizedBlob.size >= file.size && file.type === 'image/webp') {
        optimizedBlob = file;
      }
    } catch {
      setUploadProgress({ status: 'error', progress: 0, error: 'Failed to optimize image.' });
      return false;
    }

    URL.revokeObjectURL(img.src);
    const uploadContentType = optimizedBlob.type || 'image/webp';

    // Presign
    setUploadProgress({ status: 'presigning', progress: 20, error: null });

    let presignData: { uploadUrl: string; path: string };
    try {
      const res = await rawFetch(`/api/public/tenants/captures/${captureId}/${variant}/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({ contentType: uploadContentType, fileSize: optimizedBlob.size }),
      });
      if (!res.ok) {
        const errData = await res.json() as { error: string };
        setUploadProgress({ status: 'error', progress: 0, error: errData.error || 'Failed to prepare upload' });
        return false;
      }
      const presignJson = await res.json() as { data: { uploadUrl: string; path: string } };
      presignData = presignJson.data;
    } catch {
      setUploadProgress({ status: 'error', progress: 0, error: 'Network error during presign.' });
      return false;
    }

    // Upload with XHR for progress tracking
    setUploadProgress({ status: 'uploading', progress: 25, error: null });

    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const pct = Math.round(25 + (event.loaded / event.total) * 55);
            setUploadProgress((prev) => ({ ...prev, progress: Math.min(pct, 80) }));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('PUT', presignData.uploadUrl);
        xhr.setRequestHeader('Content-Type', uploadContentType);
        xhr.send(optimizedBlob);
      });
    } catch {
      setUploadProgress({ status: 'error', progress: 0, error: 'Image upload failed. Please try again.' });
      return false;
    }

    // Confirm
    setUploadProgress({ status: 'confirming', progress: 85, error: null });

    try {
      const confirmRes = await rawFetch(`/api/public/tenants/captures/${captureId}/${variant}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({ path: presignData.path, width, height }),
      });

      if (!confirmRes.ok) {
        const errData = await confirmRes.json() as { error: string };
        setUploadProgress({ status: 'error', progress: 0, error: errData.error || 'Failed to confirm image' });
        return false;
      }

      setUploadProgress({ status: 'complete', progress: 100, error: null });
      return true;
    } catch {
      setUploadProgress({ status: 'error', progress: 0, error: 'Failed to confirm upload.' });
      return false;
    }
  };

  const handleSave = async () => {
    setError('');
    setFieldErrors({});

    if (!validate()) return;

    setSaving(true);
    try {
      const payload: Record<string, any> = {
        title: title.trim(),
        description: description.trim() || null,
        captureSlug: captureSlug.trim(),
        mediaType,
        backgroundMode,
        backgroundColor,
        backgroundOverlayOpacity,
        textColorMode,
        textColor,
        buttonColorMode,
        buttonBgColor,
        buttonFgColor,
        membershipId: membershipId || null,
        vaultId: vaultId || null,
        trackId: mediaType === 'track' || mediaType === 'stream' ? (trackId || null) : null,
        youtubeUrl: mediaType === 'youtube' ? youtubeUrl.trim() : null,
        youtubeAspectRatio: mediaType === 'youtube' ? youtubeAspectRatio : null,
        facebookPixel: facebookPixel.trim() || null,
        buttonText: buttonText.trim().slice(0, 20),
        buttonDelay: buttonDelay,
        valueProp: valueProp.trim().slice(0, 120),
        collectButtonText: collectButtonText.trim().slice(0, 10),
        aboutThisTrack: aboutThisTrack.trim() || null,
        sharingPrompt: sharingPrompt.trim().slice(0, 280) || DEFAULT_SHARING_PROMPT,
        sharingSuggestions: sharingSuggestions.map((row) => ({
          id: row.id,
          label: row.label.trim().slice(0, 24),
          message: row.message.trim().slice(0, 280),
          sentiment: row.sentiment,
        })),
        audioStart: mediaType === 'track' && playbackType === 'preview' ? audioStart : 0,
        playbackType: mediaType === 'track' ? playbackType : 'preview',
        playbackLength: mediaType === 'track' && playbackType === 'preview' ? playbackLength : null,
      };

      let res: Response;
      if (isEdit) {
        res = await rawFetch(`/api/public/tenants/captures/${capture.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
          body: JSON.stringify(payload),
        });
      } else {
        // Show progress modal for creation + upload
        setUploadProgress({ status: 'creating', progress: 5, error: null });

        res = await rawFetch(`/api/public/tenants/captures`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        const errJson = await res.json() as { error: string };
        setError(errJson.error || 'Failed to save');
        setUploadProgress({ status: 'idle', progress: 0, error: null });
        return;
      }

      const captureId = isEdit
        ? capture.id
        : ((await res.json() as { data: { id: number } }).data.id);

      if (imageFile) {
        const imageUploadSuccess = await uploadCaptureImage(captureId, imageFile, 'image');
        if (!imageUploadSuccess) {
          if (!isEdit) {
            try {
              await rawFetch(`/api/public/tenants/captures/${captureId}`, {
                method: 'DELETE',
                headers: { 'X-Tenant-Slug': slug },
              });
            } catch {}
          }
          setError(uploadProgress.error || 'Image upload failed. Please try again.');
          setSaving(false);
          return;
        }
      }

      if (backgroundMode === 'image' && backgroundImageFile) {
        const backgroundImageUploadSuccess = await uploadCaptureImage(captureId, backgroundImageFile, 'background-image');
        if (!backgroundImageUploadSuccess) {
          if (!isEdit) {
            try {
              await rawFetch(`/api/public/tenants/captures/${captureId}`, {
                method: 'DELETE',
                headers: { 'X-Tenant-Slug': slug },
              });
            } catch {}
          }
          setError(uploadProgress.error || 'Background image upload failed. Please try again.');
          setSaving(false);
          return;
        }
      }

      if (mediaType === 'video' && videoFile) {
        const videoUploadSuccess = await uploadCaptureVideo(captureId, videoFile);
        if (!videoUploadSuccess) {
          if (!isEdit) {
            try {
              await rawFetch(`/api/public/tenants/captures/${captureId}`, {
                method: 'DELETE',
                headers: { 'X-Tenant-Slug': slug },
              });
            } catch {}
          }
          setError('Video upload failed. Please try again.');
          setSaving(false);
          return;
        }
      }

      if (mediaType === 'video' && videoThumbnailFile) {
        const videoThumbnailUploadSuccess = await uploadCaptureImage(captureId, videoThumbnailFile, 'video-thumbnail');
        if (!videoThumbnailUploadSuccess) {
          if (!isEdit) {
            try {
              await rawFetch(`/api/public/tenants/captures/${captureId}`, {
                method: 'DELETE',
                headers: { 'X-Tenant-Slug': slug },
              });
            } catch {}
          }
          setError(uploadProgress.error || 'Video thumbnail upload failed. Please try again.');
          setSaving(false);
          return;
        }
      }

      if (imageFile || (backgroundMode === 'image' && backgroundImageFile) || (mediaType === 'video' && videoThumbnailFile)) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        setUploadProgress({ status: 'idle', progress: 0, error: null });
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
      setUploadProgress({ status: 'idle', progress: 0, error: null });
    } finally {
      setSaving(false);
    }
  };

  const dismissProgressModal = () => {
    if (uploadProgress.status === 'error') {
      setUploadProgress({ status: 'idle', progress: 0, error: null });
      // Stay on form — capture was deleted, user can retry
    }
    if (uploadProgress.status === 'complete') {
      setUploadProgress({ status: 'idle', progress: 0, error: null });
      onSuccess();
    }
  };

  const isProgressModalVisible = uploadProgress.status !== 'idle';

  if (loadingOptions) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-electric-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      {/* Upload Progress Modal */}
      <AnimatePresence>
        {isProgressModalVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="relative w-full max-w-sm bg-[#1A1A1A] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                <h2 className="text-lg font-semibold text-white">
                  {uploadProgress.status === 'error' ? 'Upload Error' : 'Uploading Image'}
                </h2>
                {(uploadProgress.status === 'error' || uploadProgress.status === 'complete') && (
                  <button
                    onClick={dismissProgressModal}
                    className="p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <X className="w-5 h-5 text-text-secondary" />
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Error state */}
                {uploadProgress.error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mb-4"
                  >
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-red-300">{uploadProgress.error}</p>
                        <p className="text-xs text-red-300/60 mt-1">
                          The capture was created but the image upload failed.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Progress */}
                {!uploadProgress.error && (
                  <div className="space-y-4">
                    {/* Image preview thumbnail */}
                    {imagePreview && (
                      <div className="mx-auto w-24 h-24 rounded-lg overflow-hidden border border-white/10">
                        <img src={imagePreview} alt="Upload preview" className="w-full h-full object-cover" />
                      </div>
                    )}

                    {/* Progress bar */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-text-secondary">
                          {uploadProgress.status === 'creating' && 'Creating capture...'}
                          {uploadProgress.status === 'presigning' && 'Preparing upload...'}
                          {uploadProgress.status === 'uploading' && 'Uploading image...'}
                          {uploadProgress.status === 'confirming' && 'Processing...'}
                          {uploadProgress.status === 'complete' && 'Upload complete!'}
                        </span>
                        <span className="text-sm font-mono text-electric-cyan">
                          {uploadProgress.progress}%
                        </span>
                      </div>

                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full rounded-full ${
                            uploadProgress.status === 'complete' ? 'bg-green-500' : 'bg-electric-cyan'
                          }`}
                          initial={{ width: 0 }}
                          animate={{ width: `${uploadProgress.progress}%` }}
                          transition={{ duration: 0.3, ease: 'easeOut' }}
                        />
                      </div>

                      {uploadProgress.status === 'complete' && (
                        <motion.div
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-2 text-sm text-green-400"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Capture created successfully</span>
                        </motion.div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-1 hover:bg-white/5 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-text-secondary" />
            </button>
            <span className="text-xs text-text-muted font-mono uppercase tracking-wider">
              {isEdit ? 'Edit Capture' : 'New Capture'}
            </span>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2 text-sm px-4 py-2"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </nav>

      {/* Form */}
      <main className="pt-14">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-8"
          >
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* DETAILS SECTION */}
            <section>
              <h2 className="text-xs font-mono uppercase tracking-wider text-electric-cyan mb-4">
                Details
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    maxLength={80}
                    placeholder="e.g. Early Access — New Single"
                    className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors ${
                      fieldErrors.title ? 'border-red-500/50' : 'border-white/10'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {fieldErrors.title && (
                      <span className="text-xs text-red-400">{fieldErrors.title}</span>
                    )}
                    <span className="text-xs text-text-muted ml-auto">
                      {title.length}/80
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Description <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={256}
                    rows={3}
                    placeholder="Text displayed on the landing page..."
                    className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors resize-none ${
                      fieldErrors.description ? 'border-red-500/50' : 'border-white/10'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {fieldErrors.description && (
                      <span className="text-xs text-red-400">{fieldErrors.description}</span>
                    )}
                    <span className="text-xs text-text-muted ml-auto">
                      {description.length}/256
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* APPEARANCE SECTION */}
            <section>
              <h2 className="text-xs font-mono uppercase tracking-wider text-electric-cyan mb-4">
                Appearance
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Slug <span className="text-red-400">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-text-muted font-mono">/{slug}/</span>
                    <input
                      type="text"
                      value={captureSlug}
                      onChange={(e) => setCaptureSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="my-promo"
                      className={`flex-1 bg-white/5 border rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors ${
                        fieldErrors.slug ? 'border-red-500/50' : 'border-white/10'
                      }`}
                    />
                  </div>
                  {fieldErrors.slug && (
                    <span className="text-xs text-red-400 mt-1 block">{fieldErrors.slug}</span>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">
                    Background
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="backgroundMode"
                          value="automatic"
                          checked={backgroundMode === 'automatic'}
                          onChange={() => setBackgroundMode('automatic')}
                          className="accent-electric-cyan"
                        />
                        <span className="text-sm text-white">Automatic</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="backgroundMode"
                          value="image"
                          checked={backgroundMode === 'image'}
                          onChange={() => setBackgroundMode('image')}
                          className="accent-electric-cyan"
                        />
                        <span className="text-sm text-white">Image</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="backgroundMode"
                          value="solid"
                          checked={backgroundMode === 'solid'}
                          onChange={() => setBackgroundMode('solid')}
                          className="accent-electric-cyan"
                        />
                        <span className="text-sm text-white">Solid Color</span>
                      </label>
                    </div>

                    {backgroundMode === 'image' && (
                      <div className="space-y-3">
                        {backgroundImagePreview ? (
                          <div className="relative group">
                            <div className="w-full h-44 rounded-lg overflow-hidden border border-white/10">
                              <img src={backgroundImagePreview} alt="Background preview" className="w-full h-full object-cover" />
                            </div>
                            <button
                              onClick={removeBackgroundImage}
                              className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 hover:bg-black/80 transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <X className="w-4 h-4 text-white" />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => backgroundImageInputRef.current?.click()}
                            className={`flex items-center gap-3 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all hover:border-white/30 hover:bg-white/[0.02] ${
                              fieldErrors.backgroundImage ? 'border-red-500/50' : 'border-white/10'
                            }`}
                          >
                            <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center">
                              <Upload className="w-5 h-5 text-text-muted" />
                            </div>
                            <div>
                              <p className="text-sm text-white">Upload background image</p>
                              <p className="text-xs text-text-muted mt-0.5">JPEG, PNG, or WebP — Max 10MB</p>
                            </div>
                          </div>
                        )}

                        {!backgroundImagePreview && existingBackgroundImageS3Uri && (
                          <p className="text-xs text-text-muted">A background image is already attached to this capture.</p>
                        )}

                        {fieldErrors.backgroundImage && !backgroundImagePreview && (
                          <span className="text-xs text-red-400 mt-1 block">{fieldErrors.backgroundImage}</span>
                        )}

                        <input
                          ref={backgroundImageInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={handleBackgroundImageSelect}
                          className="hidden"
                        />

                        <div>
                          <label className="block text-xs text-text-muted mb-1.5">
                            Softening Overlay ({backgroundOverlayOpacity}%)
                          </label>
                          <input
                            type="range"
                            min={0}
                            max={90}
                            step={1}
                            value={backgroundOverlayOpacity}
                            onChange={(e) => setBackgroundOverlayOpacity(parseInt(e.target.value, 10))}
                            className="w-full accent-electric-cyan"
                          />
                        </div>
                      </div>
                    )}

                    {backgroundMode === 'solid' && (
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={backgroundColor}
                          onChange={(e) => setBackgroundColor(e.target.value.toUpperCase())}
                          className="h-10 w-14 rounded border border-white/10 bg-transparent p-1 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={backgroundColor}
                          onChange={(e) => setBackgroundColor(e.target.value)}
                          className="w-36 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors"
                          placeholder="#0D0D0D"
                        />
                      </div>
                    )}

                    <div className="space-y-3 pt-1 border-t border-white/10">
                      <label className="block text-sm text-text-secondary">Text Color</label>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="textColorMode"
                            value="automatic"
                            checked={textColorMode === 'automatic'}
                            onChange={() => setTextColorMode('automatic')}
                            className="accent-electric-cyan"
                          />
                          <span className="text-sm text-white">Automatic</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="textColorMode"
                            value="solid"
                            checked={textColorMode === 'solid'}
                            onChange={() => setTextColorMode('solid')}
                            className="accent-electric-cyan"
                          />
                          <span className="text-sm text-white">Solid Color</span>
                        </label>
                      </div>
                      {textColorMode === 'solid' && (
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={textColor}
                            onChange={(e) => setTextColor(e.target.value.toUpperCase())}
                            className="h-10 w-14 rounded border border-white/10 bg-transparent p-1 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={textColor}
                            onChange={(e) => setTextColor(e.target.value)}
                            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors"
                            placeholder="#FFFFFF"
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 pt-1 border-t border-white/10">
                      <label className="block text-sm text-text-secondary">Button Colors</label>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="buttonColorMode"
                            value="automatic"
                            checked={buttonColorMode === 'automatic'}
                            onChange={() => setButtonColorMode('automatic')}
                            className="accent-electric-cyan"
                          />
                          <span className="text-sm text-white">Automatic</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="buttonColorMode"
                            value="solid"
                            checked={buttonColorMode === 'solid'}
                            onChange={() => setButtonColorMode('solid')}
                            className="accent-electric-cyan"
                          />
                          <span className="text-sm text-white">Solid Colors</span>
                        </label>
                      </div>

                      {buttonColorMode === 'solid' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-text-muted mb-1.5">Foreground</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={buttonFgColor}
                                onChange={(e) => setButtonFgColor(e.target.value.toUpperCase())}
                                className="h-10 w-14 rounded border border-white/10 bg-transparent p-1 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={buttonFgColor}
                                onChange={(e) => setButtonFgColor(e.target.value)}
                                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors"
                                placeholder="#0D0D0D"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-text-muted mb-1.5">Background</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={buttonBgColor}
                                onChange={(e) => setButtonBgColor(e.target.value.toUpperCase())}
                                className="h-10 w-14 rounded border border-white/10 bg-transparent p-1 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={buttonBgColor}
                                onChange={(e) => setButtonBgColor(e.target.value)}
                                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors"
                                placeholder="#00F5FF"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAN MEMBERSHIP SECTION */}
            <section>
              <h2 className="text-xs font-mono uppercase tracking-wider text-electric-cyan mb-4">
                Fan Membership
              </h2>
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">
                  Membership <span className="text-red-400">*</span>
                </label>
                <select
                  value={membershipId || ''}
                  onChange={(e) => setMembershipId(e.target.value ? parseInt(e.target.value) : null)}
                  className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors appearance-none ${
                    fieldErrors.membership ? 'border-red-500/50' : 'border-white/10'
                  }`}
                >
                  <option value="" className="bg-[#1a1a1a]">Select a membership...</option>
                  {memberships.map((m) => (
                    <option key={m.id} value={m.id} className="bg-[#1a1a1a]">
                      {m.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.membership ? (
                  <p className="text-xs text-red-400 mt-1.5">{fieldErrors.membership}</p>
                ) : (
                  <p className="text-xs text-text-muted mt-1.5">
                    Fans who register through this Capture will join the selected membership.
                  </p>
                )}
              </div>
            </section>

            {/* MUSIC SECTION */}
            <section>
              <h2 className="text-xs font-mono uppercase tracking-wider text-electric-cyan mb-4">
                Music
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Media Type</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="mediaType"
                        value="track"
                        checked={mediaType === 'track'}
                        onChange={() => {
                          setMediaType('track');
                          setVideoFile(null);
                          setVideoFileName(null);
                          removeVideoThumbnail();
                        }}
                        className="accent-electric-cyan"
                      />
                      <span className="text-sm text-white">Track</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="mediaType"
                        value="stream"
                        checked={mediaType === 'stream'}
                        onChange={() => {
                          setMediaType('stream');
                          setTrackId(null);
                          setVideoFile(null);
                          setVideoFileName(null);
                          removeVideoThumbnail();
                        }}
                        className="accent-electric-cyan"
                      />
                      <span className="text-sm text-white">Stream</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="mediaType"
                        value="youtube"
                        checked={mediaType === 'youtube'}
                        onChange={() => {
                          setMediaType('youtube');
                          setTrackId(null);
                          setVideoFile(null);
                          setVideoFileName(null);
                          setYoutubeAspectRatio('16:9');
                          removeVideoThumbnail();
                        }}
                        className="accent-electric-cyan"
                      />
                      <span className="text-sm text-white">YouTube</span>
                    </label>
                  </div>
                </div>

                {/* Pressing selector */}
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Pressing <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={vaultId || ''}
                    onChange={(e) => {
                      const newVaultId = e.target.value ? parseInt(e.target.value) : null;
                      setVaultId(newVaultId);
                      if (mediaType === 'track' || mediaType === 'stream') {
                        // Reset track selection when vault changes
                        setTrackId(null);
                        setAudioStart(0);
                        setPlaybackLength(30);
                      }
                    }}
                    className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors appearance-none ${
                      fieldErrors.vault ? 'border-red-500/50' : 'border-white/10'
                    }`}
                  >
                    <option value="" className="bg-[#1a1a1a]">Select a pressing...</option>
                    {vaults.map((v) => (
                      <option key={v.id} value={v.id} className="bg-[#1a1a1a]">
                        {v.name || 'Untitled'} — {v.status.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.vault ? (
                    <p className="text-xs text-red-400 mt-1.5">{fieldErrors.vault}</p>
                  ) : vaults.length === 0 ? (
                    <p className="text-xs text-yellow-400/80 mt-1.5">
                      No scheduled or pressed pressings available.
                    </p>
                  ) : (
                    <p className="text-xs text-text-muted mt-1.5">
                      Only pressings in Scheduled or Pressed status are shown.
                    </p>
                  )}
                </div>

                {/* Track selector (only for track media type) */}
                {(mediaType === 'track' || mediaType === 'stream') && vaultId && (
                  <div>
                    <label className="block text-sm text-text-secondary mb-1.5">
                      {mediaType === 'stream' ? 'Stream Track' : 'Track'} <span className="text-red-400">*</span>
                    </label>
                    {loadingVaultTracks ? (
                      <div className="flex items-center gap-2 py-2.5">
                        <Loader2 className="w-4 h-4 text-electric-cyan animate-spin" />
                        <span className="text-xs text-text-muted">Loading tracks...</span>
                      </div>
                    ) : (
                      <select
                        value={trackId || ''}
                        onChange={(e) => {
                          const newTrackId = e.target.value ? parseInt(e.target.value) : null;
                          setTrackId(newTrackId);
                          // Reset preview settings when track changes
                          if (newTrackId && playbackType === 'preview') {
                            const selected = tracks.find((t) => t.id === newTrackId);
                            if (selected) {
                              if (selected.previewStart != null) setAudioStart(selected.previewStart);
                              else setAudioStart(0);
                              if (selected.previewLength != null) setPlaybackLength(selected.previewLength);
                              else setPlaybackLength(30);
                            } else {
                              setAudioStart(0);
                              setPlaybackLength(30);
                            }
                          } else {
                            setAudioStart(0);
                            setPlaybackLength(30);
                          }
                        }}
                        className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors appearance-none ${
                          fieldErrors.track ? 'border-red-500/50' : 'border-white/10'
                        }`}
                      >
                        <option value="" className="bg-[#1a1a1a]">Select a track...</option>
                        {availableTracks.map((t) => (
                          <option key={t.id} value={t.id} className="bg-[#1a1a1a]">
                            {t.title}
                          </option>
                        ))}
                      </select>
                    )}
                    {fieldErrors.track ? (
                      <p className="text-xs text-red-400 mt-1.5">{fieldErrors.track}</p>
                    ) : availableTracks.length === 0 && !loadingVaultTracks ? (
                      <p className="text-xs text-yellow-400/80 mt-1.5">
                        {mediaType === 'stream'
                          ? 'This pressing has no tracks with ISRC and streaming links.'
                          : 'This pressing has no tracks.'}
                      </p>
                    ) : null}
                  </div>
                )}

                {(mediaType === 'track' || mediaType === 'stream') && (
                  <div>
                    <label className="block text-sm text-text-secondary mb-1.5">
                      Cover Image
                    </label>
                    {imagePreview ? (
                      <div className="relative group">
                        <div className="w-full h-44 rounded-lg overflow-hidden border border-white/10">
                          <img src={imagePreview} alt="Capture cover preview" className="w-full h-full object-cover" />
                        </div>
                        <button
                          onClick={removeImage}
                          className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 hover:bg-black/80 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex items-center gap-3 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all hover:border-white/30 hover:bg-white/[0.02] ${
                          fieldErrors.image ? 'border-red-500/50' : 'border-white/10'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center">
                          <Upload className="w-5 h-5 text-text-muted" />
                        </div>
                        <div>
                          <p className="text-sm text-white">Click to select an image</p>
                          <p className="text-xs text-text-muted mt-0.5">JPEG, PNG, or WebP — Max 10MB</p>
                        </div>
                      </div>
                    )}

                    {!imagePreview && (capture?.imageS3Uri || imageFile) && (
                      <p className="text-xs text-text-muted mt-1.5">A capture cover image is attached.</p>
                    )}

                    {fieldErrors.image && !imagePreview && (
                      <span className="text-xs text-red-400 mt-1 block">{fieldErrors.image}</span>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>
                )}

                {/* Video upload (only for video media type) */}
                {mediaType === 'video' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-1.5">
                        Video Upload <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        onChange={handleVideoSelect}
                        className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors file:mr-3 file:bg-electric-cyan/20 file:text-electric-cyan file:border-0 file:rounded file:px-2 file:py-1 file:text-xs ${
                          fieldErrors.video ? 'border-red-500/50' : 'border-white/10'
                        }`}
                      />
                      {videoFileName && (
                        <p className="text-xs text-text-muted mt-1.5">Selected: {videoFileName}</p>
                      )}
                      {!videoFileName && existingVideoS3Uri && (
                        <p className="text-xs text-text-muted mt-1.5">A video is already attached to this capture.</p>
                      )}
                      {fieldErrors.video && (
                        <p className="text-xs text-red-400 mt-1.5">{fieldErrors.video}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm text-text-secondary mb-1.5">
                        Video Thumbnail <span className="text-red-400">*</span>
                      </label>
                      {videoThumbnailPreview ? (
                        <div className="relative group">
                          <div className="w-full h-44 rounded-lg overflow-hidden border border-white/10">
                            <img src={videoThumbnailPreview} alt="Video thumbnail preview" className="w-full h-full object-cover" />
                          </div>
                          <button
                            onClick={removeVideoThumbnail}
                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 hover:bg-black/80 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => videoThumbnailInputRef.current?.click()}
                          className={`flex items-center gap-3 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all hover:border-white/30 hover:bg-white/[0.02] ${
                            fieldErrors.videoThumbnail ? 'border-red-500/50' : 'border-white/10'
                          }`}
                        >
                          <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center">
                            <Upload className="w-5 h-5 text-text-muted" />
                          </div>
                          <div>
                            <p className="text-sm text-white">Upload thumbnail image</p>
                            <p className="text-xs text-text-muted mt-0.5">JPEG, PNG, or WebP — Max 10MB</p>
                          </div>
                        </div>
                      )}

                      {!videoThumbnailPreview && existingVideoThumbnailS3Uri && (
                        <p className="text-xs text-text-muted mt-1.5">A video thumbnail is already attached to this capture.</p>
                      )}

                      {fieldErrors.videoThumbnail && !videoThumbnailPreview && (
                        <p className="text-xs text-red-400 mt-1.5">{fieldErrors.videoThumbnail}</p>
                      )}

                      <input
                        ref={videoThumbnailInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={handleVideoThumbnailSelect}
                        className="hidden"
                      />
                    </div>
                  </div>
                )}

                {mediaType === 'youtube' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-1.5">
                        YouTube URL <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="url"
                        value={youtubeUrl}
                        onChange={(e) => setYoutubeUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors ${
                          fieldErrors.youtubeUrl ? 'border-red-500/50' : 'border-white/10'
                        }`}
                      />
                      {fieldErrors.youtubeUrl ? (
                        <p className="text-xs text-red-400 mt-1.5">{fieldErrors.youtubeUrl}</p>
                      ) : (
                        <p className="text-xs text-text-muted mt-1.5">
                          Paste a YouTube watch, share, shorts, or embed URL.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm text-text-secondary mb-1.5">
                        Aspect Ratio
                      </label>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="youtubeAspectRatio"
                            value="16:9"
                            checked={youtubeAspectRatio === '16:9'}
                            onChange={() => setYoutubeAspectRatio('16:9')}
                            className="accent-electric-cyan"
                          />
                          <span className="text-sm text-white">Landscape (16:9)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="youtubeAspectRatio"
                            value="9:16"
                            checked={youtubeAspectRatio === '9:16'}
                            onChange={() => setYoutubeAspectRatio('9:16')}
                            className="accent-electric-cyan"
                          />
                          <span className="text-sm text-white">Portrait (9:16)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Playback Type */}
              {mediaType === 'track' && trackId && (() => {
                const selectedTrack = tracks.find((t) => String(t.id) === String(trackId));
                const songLength = selectedTrack?.songLength || 0;
                return (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">
                        Playback Mode
                      </label>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="playbackType"
                            value="preview"
                            checked={playbackType === 'preview'}
                            onChange={() => {
                              setPlaybackType('preview');
                              // Pre-populate from track preview settings
                              if (selectedTrack?.previewStart != null) setAudioStart(selectedTrack.previewStart);
                              if (selectedTrack?.previewLength != null) setPlaybackLength(selectedTrack.previewLength);
                              else setPlaybackLength(30);
                            }}
                            className="accent-electric-cyan"
                          />
                          <span className="text-sm text-white">Preview</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="playbackType"
                            value="full"
                            checked={playbackType === 'full'}
                            onChange={() => setPlaybackType('full')}
                            className="accent-electric-cyan"
                          />
                          <span className="text-sm text-white">Full Track</span>
                        </label>
                      </div>
                      <p className="text-xs text-text-muted mt-1.5">
                        {playbackType === 'preview'
                          ? 'Plays a clip of the track on the capture page'
                          : 'Plays the entire track on the capture page'}
                      </p>
                    </div>

                    {/* Preview settings (only when Preview mode) */}
                    {playbackType === 'preview' && (
                      <div className="space-y-4 p-4 rounded-lg bg-white/[0.02] border border-white/5">
                        <div>
                          <label className="block text-sm text-text-secondary mb-1.5">
                            Audio Start (seconds)
                          </label>
                          <input
                            type="number"
                            value={audioStart}
                            onChange={(e) => {
                              const val = Math.max(0, parseInt(e.target.value) || 0);
                              const maxStart = Math.max(0, Math.floor(songLength - playbackLength));
                              setAudioStart(Math.min(val, maxStart));
                            }}
                            min={0}
                            max={Math.max(0, Math.floor(songLength - playbackLength))}
                            className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                          />
                          <p className="text-xs text-text-muted mt-1">
                            Where the preview clip begins
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm text-text-secondary mb-1.5">
                            Playback Length (seconds)
                          </label>
                          <input
                            type="number"
                            value={playbackLength}
                            onChange={(e) => {
                              const val = Math.max(30, parseInt(e.target.value) || 30);
                              const maxLength = songLength > 0 ? Math.floor(songLength - audioStart) : val;
                              setPlaybackLength(Math.min(val, maxLength));
                            }}
                            min={30}
                            max={songLength > 0 ? Math.floor(songLength - audioStart) : 9999}
                            className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                          />
                          <p className="text-xs text-text-muted mt-1">
                            {songLength > 0
                              ? `Minimum 30s — Track: ${Math.floor(songLength / 60)}:${String(Math.floor(songLength % 60)).padStart(2, '0')} — Available from start: ${Math.max(0, Math.floor(songLength - audioStart))}s`
                              : 'Minimum 30s — Song length not yet available'}
                          </p>
                          {songLength > 0 && (audioStart + playbackLength > songLength) && (
                            <p className="text-xs text-red-400 mt-1">
                              Start + length exceeds track duration. Adjust values.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </section>

            {/* CALL TO ACTION SECTION */}
            <section>
              <h2 className="text-xs font-mono uppercase tracking-wider text-electric-cyan mb-4">
                Call to Action
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Call-to-Action Text
                  </label>
                  <input
                    type="text"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value.slice(0, 20))}
                    maxLength={20}
                    placeholder="Collect this Track"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-text-muted">Text displayed on the floating button inviting listeners to collect.</p>
                    <span className="text-xs text-text-muted">{buttonText.length}/20</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Button Delay (seconds)
                  </label>
                  <input
                    type="number"
                    value={buttonDelay}
                    onChange={(e) => setButtonDelay(Math.max(0, Math.min(300, parseInt(e.target.value) || 0)))}
                    min={0}
                    max={300}
                    className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                  />
                  <p className="text-xs text-text-muted mt-1.5">
                    Seconds after playback starts before the CTA appears
                  </p>
                </div>
              </div>
            </section>

            {/* COLLECTION SECTION */}
            <section>
              <h2 className="text-xs font-mono uppercase tracking-wider text-electric-cyan mb-4">
                Collection
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Collection Message
                  </label>
                  <textarea
                    value={valueProp}
                    onChange={(e) => setValueProp(e.target.value.slice(0, 120))}
                    maxLength={120}
                    rows={2}
                    placeholder={DEFAULT_VALUE_PROP}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors resize-none"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-text-muted">Shown in the collection modal</p>
                    <span className="text-xs text-text-muted">{valueProp.length}/120</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Collection Button Text
                  </label>
                  <input
                    type="text"
                    value={collectButtonText}
                    onChange={(e) => setCollectButtonText(e.target.value.slice(0, 10))}
                    maxLength={10}
                    placeholder="Collect"
                    className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-text-muted">Text displayed on the confirmation button in the collection modal.</p>
                    <span className="text-xs text-text-muted">{collectButtonText.length}/10</span>
                  </div>
                </div>
              </div>
            </section>

            {/* SHARING SECTION */}
            <section>
              <h2 className="text-xs font-mono uppercase tracking-wider text-electric-cyan mb-4">
                Sharing
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    Sharing Prompt
                  </label>
                  <textarea
                    value={sharingPrompt}
                    onChange={(e) => setSharingPrompt(e.target.value.slice(0, 280))}
                    maxLength={280}
                    rows={2}
                    placeholder={DEFAULT_SHARING_PROMPT}
                    className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors resize-y ${
                      fieldErrors.sharingPrompt ? 'border-red-500/50' : 'border-white/10'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {fieldErrors.sharingPrompt ? (
                      <p className="text-xs text-red-400">{fieldErrors.sharingPrompt}</p>
                    ) : (
                      <p className="text-xs text-text-muted">Always shown above the share reactions on the capture page.</p>
                    )}
                    <span className="text-xs text-text-muted ml-3">{sharingPrompt.length}/280</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">
                    About This Track
                  </label>
                  <textarea
                    value={aboutThisTrack}
                    onChange={(e) => setAboutThisTrack(e.target.value.slice(0, 2000))}
                    maxLength={2000}
                    rows={5}
                    placeholder="Tell us about the meaning, mood or story behind your track..."
                    className={`w-full bg-white/5 border rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors resize-y ${
                      fieldErrors.aboutThisTrack ? 'border-red-500/50' : 'border-white/10'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {fieldErrors.aboutThisTrack ? (
                      <p className="text-xs text-red-400">{fieldErrors.aboutThisTrack}</p>
                    ) : (
                      <p className="text-xs text-text-muted">What's this song about? What feeling, story or message would you like listeners to take away?</p>
                    )}
                    <span className="text-xs text-text-muted ml-3">{aboutThisTrack.length}/2000</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleAnalyseLyrics}
                    disabled={!canAttemptLyricsAnalysis || analysingLyrics || !hasLyricsForSelectedTrack}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-sm text-white bg-white/[0.03] hover:bg-white/[0.07] disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
                  >
                    {analysingLyrics ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    Analyse Lyrics
                  </button>

                  <button
                    type="button"
                    onClick={handleGenerateSuggestions}
                    disabled={generatingSuggestions || aboutThisTrack.trim().length === 0}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-electric-cyan/40 text-sm text-electric-cyan bg-electric-cyan/10 hover:bg-electric-cyan/15 disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
                  >
                    {generatingSuggestions ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    Generate Suggestions
                  </button>
                </div>

                {!canAttemptLyricsAnalysis && (
                  <p className="text-xs text-text-muted">Select a track to enable lyrics analysis.</p>
                )}
                {canAttemptLyricsAnalysis && !hasLyricsForSelectedTrack && (
                  <p className="text-xs text-yellow-300/80">Lyrics are unavailable for this track right now. You can still complete About This Track manually.</p>
                )}
                {suggestionsNeedRefresh && (
                  <p className="text-xs text-text-muted">About This Track changed after generation. Consider regenerating suggestions if needed.</p>
                )}
                {sharingNotice && (
                  <p className="text-xs text-green-400">{sharingNotice}</p>
                )}
                {sharingError && (
                  <p className="text-xs text-red-400">{sharingError}</p>
                )}

                <div className="space-y-3 pt-1 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm text-text-secondary">Suggested Reactions</label>
                    <button
                      type="button"
                      onClick={handleAddSuggestion}
                      disabled={sharingSuggestions.length >= 8}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-white/15 text-white hover:bg-white/10 disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add
                    </button>
                  </div>

                  {fieldErrors.sharingSuggestions && (
                    <p className="text-xs text-red-400">{fieldErrors.sharingSuggestions}</p>
                  )}

                  {sharingSuggestions.length === 0 ? (
                    <p className="text-xs text-text-muted">No suggestions yet. Generate with AI or add one manually.</p>
                  ) : (
                    sharingSuggestions.map((row, index) => (
                      <div key={row.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-mono uppercase tracking-wide text-text-muted">Suggestion {index + 1}</p>
                          <button
                            type="button"
                            onClick={() => handleRemoveSuggestion(row.id)}
                            className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Remove
                          </button>
                        </div>

                        <div>
                          <label className="block text-xs text-text-secondary mb-1">Label</label>
                          <input
                            type="text"
                            value={row.label}
                            onChange={(e) => handleSuggestionChange(row.id, 'label', e.target.value)}
                            maxLength={24}
                            placeholder="Rebellious"
                            className={`w-full bg-white/5 border rounded-lg px-3 py-2 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors ${
                              fieldErrors[`suggestionLabel_${index}`] ? 'border-red-500/50' : 'border-white/10'
                            }`}
                          />
                          <div className="flex items-center justify-between mt-1">
                            {fieldErrors[`suggestionLabel_${index}`] ? (
                              <p className="text-xs text-red-400">{fieldErrors[`suggestionLabel_${index}`]}</p>
                            ) : <span />}
                            <span className="text-xs text-text-muted">{row.label.length}/24</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-text-secondary mb-1">Suggested Share Message</label>
                          <textarea
                            value={row.message}
                            onChange={(e) => handleSuggestionChange(row.id, 'message', e.target.value)}
                            rows={3}
                            maxLength={280}
                            placeholder="This is ridiculous in the best possible way. Give this a listen!"
                            className={`w-full bg-white/5 border rounded-lg px-3 py-2 text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors resize-y ${
                              fieldErrors[`suggestionMessage_${index}`] ? 'border-red-500/50' : 'border-white/10'
                            }`}
                          />
                          <div className="flex items-center justify-between mt-1">
                            {fieldErrors[`suggestionMessage_${index}`] ? (
                              <p className="text-xs text-red-400">{fieldErrors[`suggestionMessage_${index}`]}</p>
                            ) : <span />}
                            <span className="text-xs text-text-muted">{row.message.length}/280</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-text-secondary mb-1">Sentiment</label>
                          <select
                            value={row.sentiment}
                            onChange={(e) => handleSuggestionChange(row.id, 'sentiment', e.target.value)}
                            className={`w-full bg-white/5 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors ${
                              fieldErrors[`suggestionSentiment_${index}`] ? 'border-red-500/50' : 'border-white/10'
                            }`}
                          >
                            <option value="POSITIVE">POSITIVE</option>
                            <option value="NEUTRAL">NEUTRAL</option>
                            <option value="NEGATIVE">NEGATIVE</option>
                          </select>
                          {fieldErrors[`suggestionSentiment_${index}`] ? (
                            <p className="text-xs text-red-400 mt-1">{fieldErrors[`suggestionSentiment_${index}`]}</p>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            {/* TRACKING SECTION */}
            <section>
              <h2 className="text-xs font-mono uppercase tracking-wider text-electric-cyan mb-4">
                Tracking
              </h2>
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">Facebook Pixel</label>
                <input
                  type="text"
                  value={facebookPixel}
                  onChange={(e) => setFacebookPixel(e.target.value)}
                  placeholder="e.g. 123456789012345"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-text-muted text-sm font-mono focus:outline-none focus:border-electric-cyan/50 transition-colors"
                />
                <p className="text-xs text-text-muted mt-1.5">
                  Track conversions on your capture page (optional)
                </p>
              </div>
            </section>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
